import { createFileRoute } from "@tanstack/react-router";
import { AppHeader } from "@/components/AppHeader";
import { useProfile } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, ArrowDown, CheckCircle2, Clock, XCircle } from "lucide-react";
import { useMemo } from "react";
import { usePageLoadMetric } from "@/lib/diagnostics/usePageLoadMetric";

export const Route = createFileRoute("/_authenticated/history")({
  head: () => ({ meta: [{ title: "History — Brothers Club" }] }),
  component: HistoryPage,
});

type Bet = {
  id: string;
  number: number;
  amount: number;
  status: string;
  payout: number;
  game_id: string;
};

function HistoryPage() {
  const { data: profile } = useProfile();
  usePageLoadMetric("history_load", !profile);

  const { data: txns } = useQuery({
    queryKey: ["history_txns", profile?.id],
    enabled: !!profile,
    queryFn: async () => {
      const { data } = await supabase.from("wallet_transactions").select("*")
        .eq("user_id", profile!.id).order("created_at", { ascending: false }).limit(200);
      return data ?? [];
    },
  });

  // Collect all referenced bet IDs & game IDs from game transactions
  const { betIds, gameIds } = useMemo(() => {
    const b = new Set<string>();
    const g = new Set<string>();
    for (const t of txns ?? []) {
      const ids = (t.meta as any)?.bet_ids;
      if (Array.isArray(ids)) ids.forEach((x: string) => b.add(x));
      const gid = (t.meta as any)?.game_id;
      if (gid) g.add(gid);
    }
    return { betIds: Array.from(b), gameIds: Array.from(g) };
  }, [txns]);

  const { data: bets } = useQuery({
    queryKey: ["history_bets", profile?.id, betIds.length, betIds.join(",")],
    enabled: !!profile && betIds.length > 0,
    queryFn: async () => {
      // Fetch in chunks to avoid oversized `in` URLs that PostgREST rejects.
      const chunkSize = 100;
      const results: Bet[] = [];
      for (let i = 0; i < betIds.length; i += chunkSize) {
        const chunk = betIds.slice(i, i + chunkSize);
        const { data, error } = await supabase.from("bets")
          .select("id, number, amount, status, payout, game_id")
          .in("id", chunk);
        if (error) throw error;
        results.push(...(data ?? []));
      }
      return results;
    },
  });

  // Merge game IDs from txn meta AND from fetched bets (so we always resolve the real market)
  const allGameIds = useMemo(() => {
    const s = new Set<string>(gameIds);
    for (const b of bets ?? []) if (b.game_id) s.add(b.game_id);
    return Array.from(s);
  }, [gameIds, bets]);

  const { data: games } = useQuery({
    queryKey: ["history_games", allGameIds.join(",")],
    enabled: allGameIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase.from("games").select("id, name").in("id", allGameIds);
      return data ?? [];
    },
  });

  const betMap = useMemo(() => {
    const m = new Map<string, Bet>();
    for (const b of bets ?? []) m.set(b.id, b);
    return m;
  }, [bets]);

  const gameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of games ?? []) m.set(g.id, g.name);
    return m;
  }, [games]);

  const filter = (kinds: string[]) => (txns ?? []).filter((t: any) => kinds.includes(t.type));

  return (
    <div>
      <AppHeader />
      <div className="gradient-header px-4 pb-6 pt-2 -mt-1">
        <h2 className="text-lg font-black text-primary-foreground">History</h2>
        <p className="text-xs text-white/70">All your wallet & game activity.</p>
      </div>
      <div className="px-4 py-4">
        <Tabs defaultValue="all">
          <TabsList className="grid w-full grid-cols-4 rounded-full bg-muted p-1">
            <TabsTrigger value="all" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">All</TabsTrigger>
            <TabsTrigger value="add" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Add</TabsTrigger>
            <TabsTrigger value="withdraw" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Withdraw</TabsTrigger>
            <TabsTrigger value="games" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Games</TabsTrigger>
          </TabsList>
          <TabsContent value="all" className="mt-3"><List rows={txns ?? []} betMap={betMap} gameMap={gameMap} /></TabsContent>
          <TabsContent value="add" className="mt-3"><List rows={filter(["add", "referral_bonus"])} betMap={betMap} gameMap={gameMap} /></TabsContent>
          <TabsContent value="withdraw" className="mt-3"><List rows={filter(["withdraw"])} betMap={betMap} gameMap={gameMap} /></TabsContent>
          <TabsContent value="games" className="mt-3"><List rows={filter(["bet", "win", "refund", "late_bet_refund"])} betMap={betMap} gameMap={gameMap} /></TabsContent>
        </Tabs>
        <div className="h-4" />
      </div>
    </div>
  );
}

function List({ rows, betMap, gameMap }: { rows: any[]; betMap: Map<string, Bet>; gameMap: Map<string, string> }) {
  if (rows.length === 0)
    return <div className="rounded-2xl bg-card p-8 text-center text-sm text-muted-foreground shadow-card">No activity yet.</div>;
  return (
    <div className="space-y-2">
      {rows.map((t) => <TxnRow key={t.id} t={t} betMap={betMap} gameMap={gameMap} />)}
    </div>
  );
}

function TxnRow({ t, betMap, gameMap }: { t: any; betMap: Map<string, Bet>; gameMap: Map<string, string> }) {
  const isCredit = ["add", "win", "refund", "late_bet_refund", "referral_bonus"].includes(t.type);
  const isGame = ["bet", "win", "refund", "late_bet_refund"].includes(t.type);
  const label: Record<string, string> = {
    add: "Added Money", withdraw: "Withdraw", win: "Winning Amount",
    bet: "Game Entry", refund: "Refund", late_bet_refund: "Late Bet Refund",
    referral_bonus: "Referral Bonus",
  };

  // Enriched game details
  const meta = (t.meta ?? {}) as any;
  const betIds: string[] = Array.isArray(meta.bet_ids) ? meta.bet_ids : [];
  const linkedBets = betIds.map((id) => betMap.get(id)).filter(Boolean) as Bet[];
  const gameId: string | undefined = meta.game_id ?? linkedBets[0]?.game_id;
  const marketName = gameId ? gameMap.get(gameId) : undefined;

  // Simplified Game Entry card
  if (t.type === "bet") {
    const totalBetFromBets = linkedBets.reduce((s, b) => s + Number(b.amount || 0), 0);
    const totalBet = totalBetFromBets > 0 ? totalBetFromBets : Math.abs(Number(t.amount || 0));
    const status = deriveBetStatus(linkedBets);
    return (
      <div className="rounded-2xl bg-card p-4 shadow-card space-y-2">
        <p className="text-sm font-semibold text-foreground">Game Entry</p>
        <p className="text-xl font-black text-primary uppercase">{marketName ?? "Unknown Market"}</p>
        <p className="text-sm font-semibold text-foreground">Bet: ₹{totalBet.toLocaleString("en-IN")}</p>
        <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString("en-IN")}</p>
        <p className={`text-xs font-bold ${betStatusColor(status)}`}>Status: {status}</p>
      </div>
    );
  }

  const StatusIcon = t.status === "success" ? CheckCircle2 : t.status === "pending" ? Clock : XCircle;
  const statusColor = t.status === "success" ? "text-success" : t.status === "pending" ? "text-warning" : "text-destructive";
  const statusText = t.status === "success" ? "Success" : t.status === "pending" ? "Pending" : "Failed";

  return (
    <div className="rounded-2xl bg-card p-3 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${isCredit ? "bg-success/15 text-success" : "bg-destructive/15 text-destructive"}`}>
            {isCredit ? <Plus className="h-5 w-5" /> : <ArrowDown className="h-5 w-5" />}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">
              {isGame && marketName ? `${label[t.type]} · ${marketName}` : (label[t.type] ?? t.type)}
            </p>
            <p className="text-[11px] text-muted-foreground">{new Date(t.created_at).toLocaleString("en-IN")}</p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className={`text-sm font-black ${isCredit ? "text-success" : "text-destructive"}`}>
            {isCredit ? "+" : "-"}₹{Number(t.amount).toLocaleString("en-IN")}
          </p>
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase ${statusColor}`}>
            <StatusIcon className="h-3 w-3" /> {statusText}
          </span>
        </div>
      </div>
    </div>
  );
}

function deriveBetStatus(bets: Bet[]): "Pending" | "Won" | "Lost" | "Partial Win" {
  if (bets.length === 0) return "Pending";
  const statuses = bets.map((b) => b.status);
  const hasPending = statuses.includes("pending");
  const hasWon = statuses.includes("won");
  const hasLost = statuses.includes("lost");
  if (hasPending) return "Pending";
  if (hasWon && hasLost) return "Partial Win";
  if (hasWon) return "Won";
  return "Lost";
}

function betStatusColor(s: "Pending" | "Won" | "Lost" | "Partial Win") {
  return s === "Won" ? "text-success" : s === "Pending" ? "text-warning" : s === "Partial Win" ? "text-primary" : "text-destructive";
}
